import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle, Clock, Calendar, 
  ChevronDown, ChevronUp, User, 
  RotateCcw, Award, MessageSquare, Lock, AlertTriangle, Trophy, CheckSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

const BlocsCompetencesValidation = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStagiaire, setExpandedStagiaire] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCentre, setSelectedCentre] = useState('all');
  const [bulkMode, setBulkMode] = useState(false);

  // Les 3 Blocs de Compétences
  const BLOCS_COMPETENCES = [
    { id: "BC1", nom: "Projet d'animation", requiresPrerequis: false },
    { id: "BC2", nom: "Valorisation des activités", requiresPrerequis: false },
    { id: "BC3", nom: "Séance d'animation", requiresPrerequis: true }
  ];

  useEffect(() => {
    loadBCData();
  }, []);

  const loadBCData = async () => {
    try {
      const response = await axios.get('/blocs-competences/all');
      setStagiaires(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (stagiaireId, blocId, statut, dateValidation, commentaire, isRattrapage = false) => {
    try {
      const response = await axios.post('/blocs-competences/validate', {
        stagiaire_id: stagiaireId,
        bloc_id: blocId,
        statut: statut,
        date_validation: dateValidation,
        commentaire: commentaire,
        is_rattrapage: isRattrapage
      });
      
      if (response.data.all_bc_validated || response.data.diplome_obtenu) {
        toast.success('Félicitations ! Tous les blocs de compétences constitutifs au diplôme ont été validés !', {
          duration: 5000,
          icon: '🎉'
        });
      } else {
        toast.success('Bloc de compétence enregistré');
      }
      
      loadBCData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  // Filtrer par centre
  const centres = [...new Set(stagiaires.map(s => s.centre_formation).filter(Boolean))];
  
  const filteredStagiaires = stagiaires.filter(stag => {
    // Filtre par centre
    if (selectedCentre !== 'all' && stag.centre_formation !== selectedCentre) return false;
    // Filtre par statut
    if (filterStatus === 'all') return true;
    if (filterStatus === 'complete') return stag.diplome_obtenu;
    if (filterStatus === 'incomplete') return !stag.diplome_obtenu;
    return true;
  });

  // Stats
  const totalStagiaires = stagiaires.filter(s => selectedCentre === 'all' || s.centre_formation === selectedCentre).length;
  const stagDiplome = stagiaires.filter(s => s.diplome_obtenu && (selectedCentre === 'all' || s.centre_formation === selectedCentre)).length;
  const stagInProgress = totalStagiaires - stagDiplome;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="bc-validation">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-[#8DC63F]/10 p-2 rounded-lg">
              <Award className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <h2 className="text-lg font-display font-semibold text-[#0F172A]">Blocs de Compétences</h2>
              <p className="text-sm text-slate-500">Validation des BC pour l'obtention du diplôme</p>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stagDiplome}</p>
              <p className="text-xs text-slate-500">Diplômés</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-500">{stagInProgress}</p>
              <p className="text-xs text-slate-500">En cours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sélecteur de centre + Filtres */}
      <div className="p-4 border-b border-slate-100 bg-slate-50">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          {/* Sélecteur de centre */}
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-slate-600">Centre :</Label>
            <select
              value={selectedCentre}
              onChange={(e) => setSelectedCentre(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 rounded-lg bg-white"
              data-testid="select-centre"
            >
              <option value="all">Tous les centres</option>
              {centres.map(centre => (
                <option key={centre} value={centre}>{centre}</option>
              ))}
            </select>
          </div>
          
          {/* Filtres de statut */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                filterStatus === 'all' 
                  ? 'bg-[#8DC63F] text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="filter-all"
            >
              Tous ({totalStagiaires})
            </button>
            <button
              onClick={() => setFilterStatus('incomplete')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                filterStatus === 'incomplete' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="filter-incomplete"
            >
              En cours ({stagInProgress})
            </button>
            <button
              onClick={() => setFilterStatus('complete')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                filterStatus === 'complete' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="filter-complete"
            >
              Diplômés ({stagDiplome})
            </button>
            <button
              onClick={() => setBulkMode(!bulkMode)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${
                bulkMode ? 'bg-[#8DC63F] text-white' : 'bg-white text-[#0F172A] border border-[#8DC63F] hover:bg-green-50'
              }`}
              data-testid="bc-bulk-toggle"
            >
              <CheckSquare className="h-4 w-4" /> Validation en bloc
            </button>
          </div>
        </div>
      </div>

      {bulkMode && (
        <BulkBCGrid stagiaires={filteredStagiaires} BLOCS={BLOCS_COMPETENCES} onDone={loadBCData} />
      )}

      {/* Liste des stagiaires */}
      <div className="divide-y divide-slate-100 max-h-[800px] overflow-y-auto">
        {filteredStagiaires.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            Aucun stagiaire trouvé
          </div>
        ) : (
          filteredStagiaires.map((stag, index) => {
            const colors = [
              { border: 'border-l-[#8DC63F]', bg: 'bg-[#f7fdf0]', badge: 'bg-[#8DC63F]' },
              { border: 'border-l-[#3B82F6]', bg: 'bg-[#eff6ff]', badge: 'bg-[#3B82F6]' },
              { border: 'border-l-[#F59E0B]', bg: 'bg-[#fffbeb]', badge: 'bg-[#F59E0B]' },
              { border: 'border-l-[#EC4899]', bg: 'bg-[#fdf2f8]', badge: 'bg-[#EC4899]' },
              { border: 'border-l-[#8B5CF6]', bg: 'bg-[#f5f3ff]', badge: 'bg-[#8B5CF6]' },
            ];
            const color = colors[index % colors.length];
            const initials = `${(stag.prenom || '')[0] || ''}${(stag.nom || '')[0] || ''}`.toUpperCase();
            return (
            <div key={stag.stagiaire_id} className={`border-l-4 ${color.border} mb-3 rounded-lg overflow-hidden border border-slate-200`}>
              {/* Ligne stagiaire */}
              <div 
                className={`p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${
                  stag.diplome_obtenu ? 'bg-green-50/50' : color.bg
                }`}
                onClick={() => setExpandedStagiaire(expandedStagiaire === stag.stagiaire_id ? null : stag.stagiaire_id)}
                data-testid={`stagiaire-row-${stag.stagiaire_id}`}
              >
                <div className="flex items-center gap-3">
                  {stag.diplome_obtenu ? (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm bg-green-500">
                      <Trophy className="h-5 w-5 text-white" />
                    </div>
                  ) : (stag.photo_identite || stag.user_info?.photo_identite || stag.user_info?.photo_url) ? (
                    <img src={stag.photo_identite || stag.user_info?.photo_identite || stag.user_info?.photo_url}
                      alt={`${stag.prenom} ${stag.nom}`} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-white" />
                  ) : (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${color.badge}`}>
                      {initials}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-[#0F172A]">{stag.prenom} {stag.nom}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>{stag.centre_formation}</span>
                      {!stag.prerequis_validated && (
                        <span className="flex items-center gap-1 text-orange-600">
                          <AlertTriangle className="h-3 w-3" />
                          Prérequis incomplets
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                  {/* Progression */}
                  <div className="flex items-center gap-2">
                    <div className="w-12 sm:w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          stag.diplome_obtenu ? 'bg-green-500' : 'bg-[#8DC63F]'
                        }`}
                        style={{ width: `${(stag.validated_count / stag.total_bc) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-slate-600">{stag.validated_count}/{stag.total_bc}</span>
                  </div>
                  {stag.diplome_obtenu && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full hidden sm:flex items-center gap-1">
                      <Trophy className="h-3 w-3" />
                      Diplômé
                    </span>
                  )}
                  {expandedStagiaire === stag.stagiaire_id ? (
                    <ChevronUp className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Détail des BC (expandable) */}
              {expandedStagiaire === stag.stagiaire_id && (
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                  {/* Message si prérequis non validés */}
                  {!stag.prerequis_validated && (
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-orange-700">Prérequis non validés</p>
                        <p className="text-xs text-orange-600">Le stagiaire doit valider tous ses prérequis avant de pouvoir passer le BC3.</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {BLOCS_COMPETENCES.map((bloc) => {
                      const blocsData = stag.blocs_competences || {};
                      const validation = blocsData[bloc.id] || { statut: 'en_attente' };
                      return (
                        <BCRow
                          key={bloc.id}
                          bloc={bloc}
                          validation={validation}
                          stagiaireId={stag.stagiaire_id}
                          prerequisValidated={stag.prerequis_validated}
                          onValidate={handleValidate}
                        />
                      );
                    })}
                  </div>
                  
                  {/* Message diplôme ou BC manquant */}
                  {stag.diplome_obtenu ? (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                      <Trophy className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="font-semibold text-green-800">Félicitations !</p>
                      <p className="text-sm text-green-600">Vous avez obtenu tous les blocs de compétences constitutifs au diplôme</p>
                    </div>
                  ) : stag.validated_count < stag.total_bc && stag.blocs_competences && Object.values(stag.blocs_competences).some(bc => bc && bc.statut === 'rattrapage' && bc.attempt_number >= 2) && (
                    <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg text-center">
                      <AlertTriangle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                      <p className="font-semibold text-orange-700">Attention</p>
                      <p className="text-sm text-orange-600">Il vous manque {stag.total_bc - stag.validated_count} BC pour l'obtention de votre diplôme</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Composant pour une ligne de bloc de compétence
const todayISO = () => new Date().toLocaleDateString('sv-SE');
const isBCDone = (v) => v && (v.statut === 'valide' || v.statut === 'equivalence');

// Validation en bloc des BC : cocher puis valider à la date choisie (aujourd'hui par défaut)
const BulkBCGrid = ({ stagiaires, BLOCS, onDone }) => {
  const [selected, setSelected] = useState({});
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const key = (sid, b) => `${sid}||${b}`;
  const canCheck = (stag, bloc) => !isBCDone(stag.blocs_competences?.[bloc.id]) && (!bloc.requiresPrerequis || stag.prerequis_validated);
  const toggle = (k) => setSelected(prev => ({ ...prev, [k]: !prev[k] }));
  const setMany = (keys, value) => setSelected(prev => { const n = { ...prev }; keys.forEach(k => { n[k] = value; }); return n; });
  const rowKeys = (stag) => BLOCS.filter(b => canCheck(stag, b)).map(b => key(stag.stagiaire_id, b.id));
  const colKeys = (bloc) => stagiaires.filter(s => canCheck(s, bloc)).map(s => key(s.stagiaire_id, bloc.id));
  const allKeys = stagiaires.flatMap(rowKeys);
  const allChecked = (keys) => keys.length > 0 && keys.every(k => selected[k]);
  const count = allKeys.filter(k => selected[k]).length;

  const handleSubmit = async () => {
    if (!date) return toast.error('Choisissez une date');
    const todo = allKeys.filter(k => selected[k]).map(k => k.split('||'));
    setSaving(true);
    let ok = 0;
    let diplomes = 0;
    for (const [sid, blocId] of todo) {
      try {
        const res = await axios.post('/blocs-competences/validate', { stagiaire_id: sid, bloc_id: blocId, statut: 'valide', date_validation: date, commentaire: '' });
        ok++;
        if ((res.data?.all_bc_validated || res.data?.diplome_obtenu)) diplomes++;
      } catch (e) { /* continue with the others */ }
    }
    setSaving(false);
    setSelected({});
    if (ok === todo.length) toast.success(`${ok} bloc${ok > 1 ? 's' : ''} validé${ok > 1 ? 's' : ''}${diplomes ? ` - ${diplomes} diplôme${diplomes > 1 ? 's' : ''} obtenu${diplomes > 1 ? 's' : ''} !` : ''}`);
    else toast.error(`${ok}/${todo.length} blocs validés, certains ont échoué`);
    onDone();
  };

  return (
    <div className="p-4 border-b border-slate-200 bg-green-50/40" data-testid="bc-bulk-grid">
      <p className="text-xs text-slate-600 mb-3">Cochez les blocs réussis, puis cliquez sur « Valider la sélection ». Les blocs déjà validés sont en vert ; le BC3 est verrouillé tant que les prérequis ne sont pas validés.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th className="py-2 pr-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={allChecked(allKeys)} disabled={!allKeys.length} onChange={(e) => setMany(allKeys, e.target.checked)} className="h-4 w-4 accent-[#8DC63F]" data-testid="bc-bulk-check-all" />
                  Stagiaire
                </label>
              </th>
              {BLOCS.map(b => (
                <th key={b.id} className="py-2 px-2 text-center">
                  <label className="flex flex-col items-center gap-1 cursor-pointer">
                    <span className="font-semibold text-slate-700">{b.id}</span>
                    <span className="text-[10px]">{b.nom}</span>
                    <input type="checkbox" checked={allChecked(colKeys(b))} disabled={!colKeys(b).length} onChange={(e) => setMany(colKeys(b), e.target.checked)} className="h-4 w-4 accent-[#8DC63F]" data-testid={`bc-bulk-check-col-${b.id}`} />
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stagiaires.map(stag => (
              <tr key={stag.stagiaire_id} className="bg-white">
                <td className="py-2 pr-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={allChecked(rowKeys(stag))} disabled={!rowKeys(stag).length} onChange={(e) => setMany(rowKeys(stag), e.target.checked)} className="h-4 w-4 accent-[#8DC63F]" data-testid={`bc-bulk-check-row-${stag.stagiaire_id}`} />
                    <span className="font-medium text-[#0F172A]">{stag.prenom} {stag.nom}</span>
                  </label>
                </td>
                {BLOCS.map(b => {
                  const v = stag.blocs_competences?.[b.id];
                  const k = key(stag.stagiaire_id, b.id);
                  let cell;
                  if (isBCDone(v)) {
                    cell = (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle className="h-4 w-4" />{v.statut === 'equivalence' ? 'Équivalence' : (v.date_validation ? new Date(v.date_validation).toLocaleDateString('fr-FR') : 'Validé')}
                      </span>
                    );
                  } else if (!canCheck(stag, b)) {
                    cell = <span className="inline-flex items-center gap-1 text-xs text-slate-400" title="Prérequis non validés"><Lock className="h-4 w-4" />Prérequis</span>;
                  } else {
                    cell = <input type="checkbox" checked={!!selected[k]} onChange={() => toggle(k)} className="h-5 w-5 accent-[#8DC63F] cursor-pointer" data-testid={`bc-bulk-check-${stag.stagiaire_id}-${b.id}`} />;
                  }
                  return <td key={b.id} className="py-2 px-2 text-center">{cell}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
        <label className="text-xs text-slate-600 flex items-center gap-2">
          Date de validation
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 px-2 text-sm border border-slate-200 rounded bg-white" data-testid="bc-bulk-date" />
        </label>
        <Button onClick={handleSubmit} disabled={!count || saving} className="bg-[#8DC63F] hover:bg-[#7db336] text-white" data-testid="bc-bulk-submit">
          <CheckCircle className="h-4 w-4 mr-1.5" />
          {saving ? 'Validation...' : `Valider la sélection (${count})`}
        </Button>
      </div>
    </div>
  );
};

const BCRow = ({ bloc, validation, stagiaireId, prerequisValidated, onValidate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isRattrapageEditing, setIsRattrapageEditing] = useState(false);
  const [statut, setStatut] = useState(validation.statut || 'en_attente');
  const [dateValidation, setDateValidation] = useState(validation.date_validation || '');
  const [commentaire, setCommentaire] = useState(validation.commentaire || '');

  // Pour le rattrapage
  const [dateRattrapage, setDateRattrapage] = useState('');
  const [commentaireRattrapage, setCommentaireRattrapage] = useState('');

  // BC3 bloqué si prérequis non validés
  const isBlocked = bloc.requiresPrerequis && !prerequisValidated;
  
  // Vérifier si rattrapage déjà utilisé (max 2 tentatives)
  const canRetry = validation.statut === 'rattrapage' && (validation.attempt_number || 1) < 2;

  const handleSave = () => {
    if (!dateValidation && statut !== 'en_attente') {
      toast.error('Veuillez entrer la date de validation');
      return;
    }
    onValidate(stagiaireId, bloc.id, statut, dateValidation, commentaire, false);
    setIsEditing(false);
  };

  const handleRattrapageSave = (newStatut) => {
    if (!dateRattrapage) {
      toast.error('Veuillez entrer la date du rattrapage');
      return;
    }
    onValidate(stagiaireId, bloc.id, newStatut, dateRattrapage, commentaireRattrapage, true);
    setIsRattrapageEditing(false);
    setDateRattrapage('');
    setCommentaireRattrapage('');
  };

  const getStatusBadge = (s) => {
    switch (s) {
      case 'valide':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Validé</span>;
      case 'rattrapage':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">Rattrapage requis</span>;
      case 'equivalence':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">Équivalence</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">En attente</span>;
    }
  };

  // Historique des tentatives
  const history = validation.history || [];

  return (
    <div className={`p-4 rounded-lg border ${
      isBlocked
        ? 'bg-slate-100 border-slate-300 opacity-75'
        : validation.statut === 'valide' || validation.statut === 'equivalence'
        ? 'bg-green-50 border-green-200'
        : validation.statut === 'rattrapage'
        ? 'bg-orange-50 border-orange-200'
        : 'bg-white border-slate-200'
    }`} data-testid={`bc-row-${bloc.id}`}>
      {/* En-tête du bloc */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 text-xs font-bold rounded ${
              validation.statut === 'valide' || validation.statut === 'equivalence'
                ? 'bg-green-600 text-white'
                : 'bg-slate-600 text-white'
            }`}>
              {bloc.id}
            </span>
            <p className="font-semibold text-sm text-[#0F172A]">{bloc.nom}</p>
            {isBlocked && (
              <Lock className="h-4 w-4 text-slate-400" />
            )}
          </div>
          {bloc.requiresPrerequis && (
            <span className="inline-block mt-1 text-xs text-slate-500">
              Nécessite tous les prérequis validés
            </span>
          )}
          {validation.attempt_number === 2 && (
            <span className="inline-block mt-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs ml-2">
              Rattrapage effectué
            </span>
          )}
        </div>
        {!isEditing && !isRattrapageEditing && !isBlocked && (
          <div className="flex gap-2 flex-shrink-0">
            {canRetry && (
              <Button 
                size="sm" 
                onClick={() => setIsRattrapageEditing(true)}
                className="text-xs bg-orange-500 hover:bg-orange-600 text-white"
                data-testid={`btn-rattrapage-${bloc.id}`}
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Saisir rattrapage
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="text-xs"
              data-testid={`btn-edit-${bloc.id}`}
            >
              {validation.statut === 'en_attente' ? 'Valider' : 'Modifier'}
            </Button>
          </div>
        )}
      </div>

      {/* Message bloqué */}
      {isBlocked && (
        <div className="p-3 rounded-lg border border-dashed border-slate-400 bg-slate-50 text-center">
          <Lock className="h-5 w-5 text-slate-400 mx-auto mb-1" />
          <p className="text-sm text-slate-500">Bloqué - Prérequis non validés</p>
        </div>
      )}

      {/* Affichage du statut actuel */}
      {!isEditing && !isRattrapageEditing && !isBlocked && (
        <div className="space-y-2">
          {validation.statut === 'en_attente' ? (
            <div className="p-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-center">
              <p className="text-sm text-slate-500">En attente de validation</p>
              <p className="text-xs text-slate-400 mt-1">1 passage + 1 rattrapage autorisé</p>
            </div>
          ) : (
            <div className={`p-3 rounded-lg border ${
              validation.statut === 'rattrapage' 
                ? 'bg-orange-50 border-orange-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getStatusBadge(validation.statut)}
                  {validation.is_rattrapage && (
                    <span className="text-xs text-slate-500">(Rattrapage)</span>
                  )}
                </div>
                {validation.date_validation && (
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    <span>{new Date(validation.date_validation).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
              </div>
              
              {validation.commentaire && (
                <div className="mt-2 p-2 bg-white/50 rounded border border-slate-200">
                  <p className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Commentaire :
                  </p>
                  <p className="text-sm text-slate-700 italic">"{validation.commentaire}"</p>
                </div>
              )}
              
              {/* Avertissement si rattrapage nécessaire et épuisé */}
              {validation.statut === 'rattrapage' && validation.attempt_number >= 2 && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                  Rattrapage déjà effectué - Pas de tentative supplémentaire possible
                </div>
              )}
            </div>
          )}

          {/* Historique des tentatives */}
          {history.length > 1 && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-500 mb-2">Historique :</p>
              <div className="space-y-1">
                {history.map((h, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs p-2 bg-slate-100 rounded">
                    <span>{h.is_rattrapage ? 'Rattrapage' : '1er passage'}</span>
                    <span className={`px-2 py-0.5 rounded ${
                      h.statut === 'valide' || h.statut === 'equivalence' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {h.statut}
                    </span>
                    <span className="text-slate-500">
                      {h.date_validation ? new Date(h.date_validation).toLocaleDateString('fr-FR') : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Formulaire d'édition principal */}
      {isEditing && !isBlocked && (
        <div className="mt-3 space-y-3 pt-3 border-t border-slate-200">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date de passage</Label>
              <input
                type="date"
                value={dateValidation}
                onChange={(e) => setDateValidation(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
                data-testid={`input-date-${bloc.id}`}
              />
            </div>
            <div>
              <Label className="text-xs">Résultat</Label>
              <select
                value={statut}
                onChange={(e) => setStatut(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
                data-testid={`select-statut-${bloc.id}`}
              >
                <option value="en_attente">En attente</option>
                <option value="valide">Validé</option>
                <option value="rattrapage">Rattrapage nécessaire</option>
                <option value="equivalence">Équivalence</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Commentaire (optionnel)</Label>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Observations..."
              className="w-full mt-1 h-16 px-2 py-1 text-sm border border-slate-200 rounded resize-none"
              data-testid={`textarea-comment-${bloc.id}`}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
              Annuler
            </Button>
            <Button 
              size="sm" 
              onClick={handleSave} 
              className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
              data-testid={`btn-save-${bloc.id}`}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {/* Formulaire pour le rattrapage */}
      {isRattrapageEditing && !isBlocked && (
        <div className="mt-3 space-y-3 pt-3 border-t border-orange-200 bg-orange-50/50 p-3 rounded">
          <p className="text-sm font-medium text-orange-700">Validation du rattrapage</p>
          
          <div>
            <Label className="text-xs">Date du rattrapage</Label>
            <input
              type="date"
              value={dateRattrapage}
              onChange={(e) => setDateRattrapage(e.target.value)}
              className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
            />
          </div>

          <div>
            <Label className="text-xs">Commentaire (optionnel)</Label>
            <textarea
              value={commentaireRattrapage}
              onChange={(e) => setCommentaireRattrapage(e.target.value)}
              placeholder="Observations du rattrapage..."
              className="w-full mt-1 h-16 px-2 py-1 text-sm border border-slate-200 rounded resize-none"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setIsRattrapageEditing(false)}>
              Annuler
            </Button>
            <Button 
              size="sm" 
              onClick={() => handleRattrapageSave('rattrapage')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Échec (pas de 2ème rattrapage)
            </Button>
            <Button 
              size="sm" 
              onClick={() => handleRattrapageSave('valide')}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Validé
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlocsCompetencesValidation;
